/**
 * Main App component for trAInedup.ai
 * Sets up routing, authentication, and global providers
 */

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { DashboardLayout } from './components/dashboard/DashboardLayout';
import ConsentBanner from './components/common/ConsentBanner';
import { ScrollToTop } from './components/common/ScrollToTop';
import { PreLaunchPage } from './pages/PreLaunchPage';
import { HomePage } from './pages/HomePage';
import { HomePageRouter } from './pages/HomePageRouter';
import { AboutPage } from './pages/AboutPage';
import { PricingPage } from './pages/PricingPage';
import { TwelveMonthPackageRegisterPage } from './pages/TwelveMonthPackageRegisterPage';
import { ContactPage } from './pages/ContactPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { DashboardPage } from './pages/DashboardPage';
import { ProfilePage } from './pages/ProfilePage';
import { CoursesPage } from './pages/CoursesPage';
import { CourseCatalogPage } from './pages/CourseCatalogPage';
import { BlogPage } from './pages/BlogPage';
import { BlogPostPage } from './pages/BlogPostPage';
import { CoursesInProgressPage } from './pages/CoursesInProgressPage';
import { CompletedCoursesPage } from './pages/CompletedCoursesPage';
import { MessagesPage } from './pages/MessagesPage';
import { UnauthorizedPage } from './pages/UnauthorizedPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import CommunityPage from './pages/CommunityPage';
import { CourseManagementPage } from './pages/CourseManagementPage';
import { CourseCreationForm } from './components/courses/CourseCreationForm';
import { CourseDetailPage } from './pages/CourseDetailPage';
import { CertificatePage } from './pages/CertificatePage';
import { ModuleViewPage } from './pages/ModuleViewPage';
import { ProgressPage } from './pages/ProgressPage';
import { ROUTES, UserRole, GA_TRACKING_ID } from './utils/constants';
import { fetchAndCacheCurrency } from './utils/currency';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { AdminAffiliatesPage } from './pages/AdminAffiliatesPage';
import { AdminEmailManagementPage } from './pages/AdminEmailManagementPage';
import { AdminSupportPage } from './pages/AdminSupportPage';
import { SupportPage } from './pages/SupportPage';
import { EmailFlowVisualizationPage } from './pages/admin/EmailFlowVisualizationPage';
import { EmailFlowChangePage } from './pages/admin/EmailFlowChangePage';
import { ActivationCodesPage } from './pages/admin/ActivationCodesPage';
import { ActivationRedemptionsPage } from './pages/admin/ActivationRedemptionsPage';
import { AdminBlogPage } from './pages/admin/AdminBlogPage';
import AssessmentReviewPage from './pages/admin/AssessmentReviewPage';
import SystemHealthPage from './pages/admin/SystemHealthPage';
import SkillsAssessmentPage from './pages/SkillsAssessmentPage';
import LearningPlanPage from './pages/LearningPlanPage';
import LearningJourneyPage from './pages/LearningJourneyPage';
import { BillingPage } from './pages/BillingPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { EmailVerificationSentPage } from './pages/EmailVerificationSentPage';
import { EmailVerificationRequiredPage } from './pages/EmailVerificationRequiredPage';
import { CompleteRegistrationPage } from './pages/CompleteRegistrationPage';
import { CorporateRequestPage } from './pages/CorporateRequestPage';
import { UnsubscribePage } from './pages/UnsubscribePage';
import AIReadinessPage from './pages/AIReadinessPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { OfferLabPage } from './pages/OfferLabPage';
import { AffiliateRegistrationPage } from './pages/AffiliateRegistrationPage';
import { AffiliateLandingPage } from './pages/AffiliateLandingPage';
import { AffiliateDashboardPage } from './pages/AffiliateDashboardPage';
import { AnalyticsTracker } from './components/common/AnalyticsTracker';
import { AffiliateTracker } from './components/common/AffiliateTracker';
import { initializeGtag } from './utils/gtag';

/**
 * Main application component
 * Configures routing for all pages and provides global context
 */
function App() {
  // Initialize Google Analytics on mount (with consent denied by default)
  useEffect(() => {
    initializeGtag(GA_TRACKING_ID);
  }, []);

  // Detect currency from IP geolocation on first visit; cached for subsequent loads
  useEffect(() => {
    fetchAndCacheCurrency().then((changed) => {
      if (changed) window.location.reload();
    });
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <SubscriptionProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <ScrollToTop />
            <AnalyticsTracker />
            <AffiliateTracker />
            <div className="App">
              <ConsentBanner />
              <Routes>
              {/* Main App Routes */}
              <Route path="/" element={<HomePageRouter />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/twelve-month-package" element={<TwelveMonthPackageRegisterPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/skills-assessment" element={<SkillsAssessmentPage />} />
              <Route
                path="/learning-plan"
                element={
                  <ProtectedRoute>
                    <LearningPlanPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/learning-journey"
                element={
                  <ProtectedRoute>
                    <LearningJourneyPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Landing Page (moved from root) */}
              <Route path="/landing-page" element={<PreLaunchPage />} />
              
              {/* Legal & Other Pages */}
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/corporate-request" element={<CorporateRequestPage />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/ai-readiness" element={<ProtectedRoute><AIReadinessPage /></ProtectedRoute>} />
              {/* Community */}
              <Route
                path="/community"
                element={
                  <ProtectedRoute>
                    <CommunityPage />
                  </ProtectedRoute>
                }
              />

              {/* Public Courses Page */}
              <Route path="/courses" element={<CoursesPage />} />
              <Route path="/course-catalog" element={<CourseCatalogPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/courses/:courseId" element={<CourseDetailPage />} />
              <Route path="/certificate/:courseId" element={<CertificatePage />} />
              
              {/* Protected Module View Routes */}
              <Route
                path="/courses/:courseId/modules/:moduleId"
                element={
                  <ProtectedRoute>
                    <ModuleViewPage />
                  </ProtectedRoute>
                }
              />
              
              {/* Authentication Routes */}
              <Route path={ROUTES.login} element={<LoginPage />} />
              <Route path={ROUTES.register} element={<RegisterPage />} />
              
              {/* Offer Lab Routes */}
              <Route path="/oloffer" element={<OfferLabPage />} />
              <Route path="/affiliate" element={<AffiliateLandingPage />} />
              <Route 
                path="/affiliateregistration" 
                element={
                  <ProtectedRoute>
                    <AffiliateRegistrationPage />
                  </ProtectedRoute>
                } 
              />
              <Route path={ROUTES.forgotPassword} element={<ForgotPasswordPage />} />
              <Route path={ROUTES.resetPassword} element={<ResetPasswordPage />} />
              
              {/* Email Verification Routes */}
              <Route path={ROUTES.verifyEmail} element={<VerifyEmailPage />} />
              <Route path={ROUTES.emailVerificationSent} element={<EmailVerificationSentPage />} />
              <Route path={ROUTES.emailVerificationRequired} element={<EmailVerificationRequiredPage />} />
              <Route path="/complete-registration" element={<CompleteRegistrationPage />} />
              
              {/* Unauthorized Access */}
              <Route path="/unauthorized" element={<UnauthorizedPage />} />
              
              {/* Protected Dashboard Routes */}
              <Route
                path="/dashboard/*"
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="courses" element={<CoursesPage />} />
                <Route path="courses-in-progress" element={<CoursesInProgressPage />} />
                <Route path="courses-completed" element={<CompletedCoursesPage />} />
                <Route path="affiliate" element={<AffiliateDashboardPage />} />
                <Route path="progress" element={<ProgressPage />} />
                <Route path="messages" element={<MessagesPage />} />
                <Route path="support" element={<SupportPage />} />

                {/* Billing Route */}
                <Route path="billing" element={<BillingPage />} />
                
                {/* Admin Routes */}
                <Route
                  path="admin/users"
                  element={
                    <ProtectedRoute requiredRole={UserRole.ADMIN}>
                      <AdminUsersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/analytics"
                  element={
                    <ProtectedRoute requiredRole={UserRole.ADMIN}>
                      <AdminDashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/affiliates"
                  element={
                    <ProtectedRoute requiredRole={UserRole.ADMIN}>
                      <AdminAffiliatesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/activation-codes"
                  element={
                    <ProtectedRoute requiredRole={UserRole.ADMIN}>
                      <ActivationCodesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/activation-redemptions"
                  element={
                    <ProtectedRoute requiredRole={UserRole.ADMIN}>
                      <ActivationRedemptionsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/assessments"
                  element={
                    <ProtectedRoute requiredRole={UserRole.ADMIN}>
                      <AssessmentReviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/emails"
                  element={
                    <ProtectedRoute requiredRole={UserRole.ADMIN}>
                      <AdminEmailManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/emails/flow"
                  element={
                    <ProtectedRoute requiredRole={UserRole.ADMIN}>
                      <EmailFlowVisualizationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/emails/flow/change"
                  element={
                    <ProtectedRoute requiredRole={UserRole.ADMIN}>
                      <EmailFlowChangePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/support"
                  element={
                    <ProtectedRoute requiredRole={UserRole.ADMIN}>
                      <AdminSupportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/health"
                  element={
                    <ProtectedRoute requiredRole={UserRole.ADMIN}>
                      <SystemHealthPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/blog"
                  element={
                    <ProtectedRoute requiredRole={UserRole.ADMIN}>
                      <AdminBlogPage />
                    </ProtectedRoute>
                  }
                />
                {/* Course Management Routes (Admin & Instructor) */}
                <Route
                  path="admin/courses"
                  element={
                    <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.INSTRUCTOR]}>
                      <CourseManagementPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/courses/create"
                  element={
                    <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.INSTRUCTOR]}>
                      <CourseCreationForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/courses/:id/edit"
                  element={
                    <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.INSTRUCTOR]}>
                      <CourseCreationForm />
                    </ProtectedRoute>
                  }
                />
                
                {/* TODO: Add more protected routes */}
                {/* <Route path="courses" element={<CoursesPage />} />
                <Route path="course/:id" element={<CoursePage />} />
                <Route path="messages" element={<MessagesPage />} />
                <Route path="experts" element={<ExpertsPage />} />
                <Route path="billing" element={<BillingPage />} /> */}
              </Route>
              
              {/* Admin Redirects */}
              <Route path="/admin" element={<Navigate to="/dashboard/admin/users" replace />} />
              <Route path="/admin/users" element={<Navigate to="/dashboard/admin/users" replace />} />
              <Route path="/admin/analytics" element={<Navigate to="/dashboard/admin/analytics" replace />} />
              <Route path="/admin/blog" element={<Navigate to="/dashboard/admin/blog" replace />} />
              <Route path="/admin/courses" element={<Navigate to="/dashboard/admin/courses" replace />} />
              <Route path="/admin/courses/create" element={<Navigate to="/dashboard/admin/courses/create" replace />} />
              
              {/* Legacy route redirects */}
              <Route
                path={ROUTES.dashboard}
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.profile}
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              
              {/* Catch-all route for 404 */}
              <Route path="*" element={<HomePage />} />
            </Routes>
          </div>
        </Router>
      </SubscriptionProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
